// Group package-snapshot items by their `receipt_bucket` and compute the
// per-bucket subtotal + IGST/CGST amounts from the per-bucket GST percentages
// stored on the package snapshot. Used by the 3 printable receipts (ex_showroom,
// helmet, accessories) on a sale row.

const BUCKET_LABEL_FALLBACK = {
  "Ex-showroom (no tax)": "ex_showroom",
  "Handling Charges": "ex_showroom",
  "Life Tax": "ex_showroom",
  "TR Charges": "ex_showroom",
  Hypothecation: "ex_showroom",
  IGST: "ex_showroom",
  CGST: "ex_showroom",
  Helmet: "helmet",
  Accessories: "accessories",
  "Additional Accessories": "accessories",
};

const TAX_LABELS = new Set(["IGST", "CGST"]);

// Items whose LABEL marks them as administrative/internal — never appear on
// the printed invoices even though they live in the helmet/accessories buckets
// in the package master.
const INVOICE_EXCLUDED_LABELS = new Set(["Additional Accessories"]);

/**
 * @param {Array} packages sale.packages snapshot
 * @param {"ex_showroom"|"helmet"|"accessories"} bucket
 * Returns { lines, subtotal, igst_pct, cgst_pct, igst_amt, cgst_amt, discount_amt, gross_total, total }
 *
 * Notes:
 * - ELECTRIC packages carry the full pre-subsidy ex-showroom value. The
 *   package-level company_discount is applied as the sale discount and is
 *   never printed on this invoice.
 *   Handling / Life Tax / TR / Hypothecation stay out of the vehicle total.
 * - For helmet/accessories buckets, we sum priced items and apply the
 *   package-level helmet_*_pct / accessories_*_pct from receipt_gst.
 */
export function buildReceiptBucket(packages, bucket) {
  const lines = [];
  let stampedIgstPct = 0;
  let stampedCgstPct = 0;
  let injectedIgst = 0; // for ex_showroom: tax already in the items list
  let injectedCgst = 0;

  for (const p of packages || []) {
    const items = p.items_included || [];
    for (const it of items) {
      const b = it.receipt_bucket || BUCKET_LABEL_FALLBACK[it.label] || "other";
      if (b !== bucket) continue;
      // Skip line items that are excluded from invoicing (e.g. "Additional
      // Accessories" is a holding bucket for non-billable add-ons).
      if (INVOICE_EXCLUDED_LABELS.has(it.label)) continue;
      const price = Number(it.price) || 0;
      if (TAX_LABELS.has(it.label)) {
        if (it.label === "IGST") injectedIgst += price;
        else if (it.label === "CGST") injectedCgst += price;
        lines.push({ label: it.label, price, isTax: true, hsn_code: it.hsn_code || "" });
      } else {
        lines.push({ label: it.label, price, isTax: false, hsn_code: it.hsn_code || "" });
      }
    }
    const gst = (p.receipt_gst || {})[bucket] || {};
    stampedIgstPct = Math.max(stampedIgstPct, Number(gst.igst_pct) || 0);
    stampedCgstPct = Math.max(stampedCgstPct, Number(gst.cgst_pct) || 0);
  }

  const taxLines = lines.filter((l) => l.isTax);
  const itemLines = lines.filter((l) => !l.isTax);
  const enteredSubtotal = itemLines.reduce((s, l) => s + l.price, 0);
  const exShowroomLines = bucket === "ex_showroom"
    ? itemLines.filter((l) => l.label === "Ex-showroom (no tax)")
    : [];
  const hasCanonicalExShowroom = bucket === "ex_showroom" && exShowroomLines.length > 0;
  const customerExShowroomTotal = hasCanonicalExShowroom
    ? exShowroomLines.reduce((s, l) => s + l.price, 0) + taxLines.reduce((s, l) => s + l.price, 0)
    : enteredSubtotal;
  const grossBeforeDiscount = customerExShowroomTotal;
  let taxableSubtotal = enteredSubtotal;

  // Helmet/accessories sticker prices are GST-inclusive; ex-showroom package
  // totals are also GST-inclusive per the package master. Back-calculate the
  // taxable base from the total and the stamped tax rate.
  let igstAmt = 0;
  let cgstAmt = 0;
  let igstPct = stampedIgstPct;
  let cgstPct = stampedCgstPct;
  let derivedSubtotal = taxableSubtotal;
  let total = taxableSubtotal;
  const combined = igstPct + cgstPct;

  if (hasCanonicalExShowroom) {
    derivedSubtotal = combined > 0
      ? Math.round((grossBeforeDiscount / (1 + combined / 100)) * 100) / 100
      : grossBeforeDiscount;
    igstAmt = Math.round((derivedSubtotal * igstPct) / 100 * 100) / 100;
    cgstAmt = cgstPct > 0
      ? Math.round((grossBeforeDiscount - derivedSubtotal - igstAmt) * 100) / 100
      : 0;
    if (cgstPct === 0) igstAmt = Math.round((grossBeforeDiscount - derivedSubtotal) * 100) / 100;
    total = grossBeforeDiscount;
  } else if (bucket === "ex_showroom") {
    // Legacy snapshots without the canonical ex-showroom label keep the
    // previous behaviour: non-tax bucket lines are the base, injected tax
    // lines are the tax amounts.
    igstAmt = injectedIgst;
    cgstAmt = injectedCgst;
    total = taxableSubtotal + igstAmt + cgstAmt;
  } else if (combined > 0 && taxableSubtotal > 0) {
    derivedSubtotal = Math.round((taxableSubtotal / (1 + combined / 100)) * 100) / 100;
    igstAmt = Math.round((derivedSubtotal * igstPct) / 100 * 100) / 100;
    cgstAmt = Math.round((derivedSubtotal * cgstPct) / 100 * 100) / 100;
  }

  return {
    lines: itemLines,
    tax_lines: taxLines,
    // `subtotal` in the response is the TAXABLE base (ex-GST) so the printed
    // receipt can show the correct "Unit Cost / Taxable" column.
    subtotal: Math.round(derivedSubtotal * 100) / 100,
    // Original entered (GST-inclusive) total — used to show the per-line
    // sticker price on helmet/accessories receipts.
    entered_total: Math.round((bucket === "ex_showroom" ? customerExShowroomTotal : taxableSubtotal) * 100) / 100,
    igst_pct: igstPct,
    cgst_pct: cgstPct,
    igst_amt: Math.round(igstAmt * 100) / 100,
    cgst_amt: Math.round(cgstAmt * 100) / 100,
    discount_amt: 0,
    gross_total: Math.round(total * 100) / 100,
    total: Math.round(total * 100) / 100,
    has_content: itemLines.length > 0 || taxLines.length > 0,
    // Whether the bucket prices were captured GST-inclusive (helmet/accessories)
    // vs. exclusive (ex_showroom). Lets the renderer pick the right layout.
    inclusive: bucket !== "ex_showroom",
  };
}

export const RECEIPT_BUCKET_TITLES = {
  ex_showroom: "Ex-showroom Vehicle Invoice",
  helmet: "Helmet Receipt",
  accessories: "Accessories Receipt",
};
